import { t } from '@avalara/bat-apps-shared/i18n';
import { SCol, SContainer, SIcon, SRow } from '@avalara/skylab-sdk/react';

import type { Dispatch } from '@avalara/types/actions';

import { Fragment, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { formatPrice } from '../../../shared/helpers';
import { getRiskAssessmentSalestax } from '../../lib/side-effects/avatax';
import { getRiskAssessmentPricingData } from '../../reducers/aggregate-logic';
import { selectState } from '../../selectors';

// Styles
import { CartInfoContainer, QuantityWrapper } from '../styled/components/cart';

const CartInfo = () => {
  const dispatch = useDispatch<Dispatch>();
  const state = useSelector(selectState);
  const {
    account,
    order: { discountCode },
  } = state;
  const { salestaxDisplayText, subTotal, totalPrice, basePrice, discountAmount, discountPercent, discountName } =
    getRiskAssessmentPricingData(state);

  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    dispatch(getRiskAssessmentSalestax());
  }, [account.accountId, subTotal]);

  const cartInfoTitle =
    discountCode != null && discountPercent === 100
      ? 'Sales tax risk assessment report'
      : 'Sales tax risk assessment report and consultation';

  return (
    <SContainer className='pad-all-none'>
      <CartInfoContainer className='pad-top-md pad-left-md pad-right-md pad-bottom-md justify-content-center'>
        <div id='cart-info-header' className='pad-bottom-md'>
          {t('client.general.your-order')}
        </div>
        <SRow className='justify-content-space align-items-center cart-info-text pad-all-xs'>
          <SCol span='xl-10 lg-10 6'>{cartInfoTitle}</SCol>
          <SCol span='xl-2 lg-2 4' className='text-right'>
            <QuantityWrapper>
              <span style={discountPercent && discountPercent > 0 ? { textDecoration: 'line-through' } : {}}>
                {formatPrice(basePrice)}
              </span>
            </QuantityWrapper>
          </SCol>
        </SRow>
        {!!discountPercent && discountPercent > 0 && (
          <Fragment>
            <SRow className='justify-content-space align-items-center cart-info-text pad-all-xs'>
              <SCol span='8' className='hide-overflow'>
                <span className='pad-right-sm'>{t('client.general.discount')}</span>
                {discountName && (
                  <span className='no-wrap' title={discountName}>
                    <SIcon name='tag' aria-hidden='true' className='pad-right-xs' />
                    {discountName}
                  </span>
                )}
              </SCol>
              <SCol span='4' className='text-right align-self-start'>
                <QuantityWrapper>
                  <span>- {discountAmount}</span>
                </QuantityWrapper>
              </SCol>
            </SRow>
            <SRow className='justify-content-space align-items-center cart-info-text pad-all-xs'>
              <SCol span='xl-10 lg-10 6' className='align-self-end'>
                {t('client.general.your-price')}
              </SCol>
              <SCol span='xl-2 lg-2 4' className='text-right'>
                <hr className='pad-bottom-sm' />
                <QuantityWrapper>
                  <span>{formatPrice(subTotal)}</span>
                </QuantityWrapper>
              </SCol>
            </SRow>
          </Fragment>
        )}
        <SRow className='justify-content-space align-items-center cart-info-text pad-all-xs'>
          <SCol span='4'>{t('client.general.sales-tax')}</SCol>
          <SCol span='4' className='text-right'>
            <QuantityWrapper>
              <span className={/calculated/i.test(salestaxDisplayText) ? 'text-xs' : ''}>{salestaxDisplayText}</span>
            </QuantityWrapper>
          </SCol>
        </SRow>
        <hr />
        <SRow className='justify-content-space align-items-center cart-info-text font-bold pad-all-xs'>
          <SCol span='xl-4 xl-4 2'>{t('client.general.total')}:</SCol>
          <SCol id='sales-tax-text' span='4' className='text-right'>
            {formatPrice(totalPrice)}
          </SCol>
        </SRow>
      </CartInfoContainer>
    </SContainer>
  );
};

export default CartInfo;
